import { useCallback, useContext, useEffect, useState } from 'react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	useFirePostOfficeTrigger,
	useTrigger,
} from '@atlassian/post-office-context/post-office-trigger';

import { HydrationAbortError } from '@post-office/errors';
import { ConfluenceExperimentsContext } from '@post-office/growth-common/confluence-experiments-context';
import { CANONICAL_TEMPLATE_KEYS } from '@post-office/growth-common/confluence-experiments-context/template-xflow';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { useHandleDismiss } from '@post-office/message-templates-common--growth-starburst/hooks/use-handle-dismiss';
import { getHomeTwcXflowModalLink } from '@post-office/message-templates-common--growth-starburst/utils/get-home-twc-xflow-modal-link';
import { type MessageTemplateContext } from '@post-office/message-template--twc-sidenav-banner';
import { type WaitForTriggerFunction } from '@post-office/placement-contexts/hooks/use-wait-for-trigger';
import {
	type BackendPlacementMessage,
	type FrontendPlacementMessage,
	noValidation,
} from '@post-office/placement-common-v2';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
	type AnyFrontendStage,
} from '@post-office/placement-common-v2/create-placement/types';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import { TEMPLATE_XFLOW_EXPERIMENT_KEY } from '@post-office/placements--growth/confluence-side-nav/contexts/post-office-side-nav-message-provider';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

import { GenericBanner } from '../../../components/generic-banner';
import { PersonalizedBanner } from '../../../components/personalized-banner';
import { type RecommendedApp } from '../../../index';
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import {
	getAtlassianHomeUrl,
	getLoomOnboardingUrl,
	getOnboardingUrl,
	resolveRedirectTo,
} from '../../../utils/get-onboarding-url';

const MISSING_APP_EXPERIMENT = 'twcg_missing_app_activation_banner';
const TEMPLATE_XFLOW_GATE = 'twcg-2112-twc-xflow-conf-pages-templates-gate';
const TEMPLATE_VIEW_TRIGGER_ID = 'twcg-2112-template-view-trigger';
const TEMPLATE_APPLIED_TRIGGER_ID = 'twcg-2112-template-applied-trigger';
const TEMPLATE_XFLOW_DELAY_MS = 3_000;

const templateXflowMessages = defineMessages({
	meetingNotesHeading: {
		id: 'twc.sidenav.banner.template.xflow.meeting.notes.heading',
		defaultMessage: 'Automate meeting notes',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the Meeting Notes template.',
	},
	projectPlanHeading: {
		id: 'twc.sidenav.banner.template.xflow.project.plan.heading',
		defaultMessage: 'Instantly create a project plan',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the Project Plan template.',
	},
	eowStatusReportHeading: {
		id: 'twc.sidenav.banner.template.xflow.eow.status.report.heading',
		defaultMessage: 'Instantly create reports',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the End of Week Status Report template.',
	},
	decisionsHeading: {
		id: 'twc.sidenav.banner.template.xflow.decisions.heading',
		defaultMessage: 'Instantly capture decisions',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the Decisions template.',
	},
	requirementsRaciHeading: {
		id: 'twc.sidenav.banner.template.xflow.requirements.raci.heading',
		defaultMessage: 'Instantly capture requirements',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the Requirements RACI template.',
	},
	genericHeading: {
		id: 'twc.sidenav.banner.template.xflow.generic.heading',
		defaultMessage: 'Don’t start from scratch',
		description:
			'Heading for the Loom cross-flow banner on a supported Confluence template without dedicated copy.',
	},
	tryItFree: {
		id: 'twc.sidenav.banner.template.xflow.try.it.free',
		defaultMessage: 'Try it free',
		description: 'CTA text for opening the Teamwork Collection cross-flow modal.',
	},
	notNow: {
		id: 'twc.sidenav.banner.template.xflow.not.now',
		defaultMessage: 'Not now',
		description: 'Action text for hiding the Teamwork Collection cross-flow banner for now.',
	},
});

const templateXflowHeadingByBlueprintModuleCompleteKey: Readonly<
	Record<string, MessageDescriptor>
> = {
	[CANONICAL_TEMPLATE_KEYS.MEETING_NOTES]: templateXflowMessages.meetingNotesHeading,
	[CANONICAL_TEMPLATE_KEYS.PROJECT_PLAN]: templateXflowMessages.projectPlanHeading,
	[CANONICAL_TEMPLATE_KEYS.EOW_STATUS_REPORT]: templateXflowMessages.eowStatusReportHeading,
	[CANONICAL_TEMPLATE_KEYS.DECISIONS]: templateXflowMessages.decisionsHeading,
	[CANONICAL_TEMPLATE_KEYS.REQUIREMENTS_RACI]: templateXflowMessages.requirementsRaciHeading,
	[CANONICAL_TEMPLATE_KEYS.RETROSPECTIVES]: templateXflowMessages.genericHeading,
	[CANONICAL_TEMPLATE_KEYS.BUSINESS_STATUS_UPDATE]: templateXflowMessages.genericHeading,
	[CANONICAL_TEMPLATE_KEYS.ONE_ON_ONE_MEETING]: templateXflowMessages.genericHeading,
	[CANONICAL_TEMPLATE_KEYS.RECURRING_MEETING_MINUTES]: templateXflowMessages.genericHeading,
	[CANONICAL_TEMPLATE_KEYS.PROJECT_STATUS]: templateXflowMessages.genericHeading,
};

type ExperimentArm = 'control' | 'generic' | 'personalized';

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	onboardingUrl: string;
	messageInstanceId: string;
	locale: string;
	redirectTo: 'jira' | 'confluence';
	arm: ExperimentArm;
	experimentKey?: string;
	targetTenantId?: string;
	collectionEntitlementId?: string;
	waitForTrigger?: WaitForTriggerFunction;
};

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: noValidation(),
		stage: () => ({
			goalMetadata: {
				primaryGoal: {
					goalId: 'clickthrough',
				},
			},
		}),
	},
	hydrate: {
		validate: assertMessageTemplateContext,
		stage: async ({ message, request }) => {
			const { logger, orchestrationContext, statsig } = request;
			const { messageInstanceId, messageTemplateId, triggerId } = message;
			const { cloudId: tenantId, experimentKey, explicitEnableRequired } = message.context;
			const isTemplateXflowMessage =
				experimentKey === TEMPLATE_XFLOW_EXPERIMENT_KEY && explicitEnableRequired === true;
			const { locale } = orchestrationContext;

			const {
				recommendedApp,
				actionTier,
				targetTenantId,
				collectionEntitlementId,
				orgId,
				twcEntitlementId,
			} = message.context;

			if (!tenantId || typeof tenantId !== 'string') {
				logger.error(
					{ messageInstanceId, messageTemplateId, triggerId },
					'Tenant ID not available',
				);
				request.metrics
					.counter('twc-sidenav-banner.conf.error.count', { reason: 'tenant-id-not-available' })
					.incr();
				throw new Error('Tenant ID not available');
			}

			const env = request.executionRuntime.getEnvironmentType();
			const resolvedOrgId = typeof orgId === 'string' ? orgId : orchestrationContext.orgId;

			if (isTemplateXflowMessage && !resolvedOrgId) {
				logger.error(
					{ messageInstanceId, messageTemplateId, triggerId },
					'Org ID not available for template xflow message',
				);
				request.metrics
					.counter('twc-sidenav-banner.conf.error.count', {
						reason: 'template-xflow-org-id-not-available',
					})
					.incr();
				throw new Error('Org ID not available for template xflow message');
			}

			const armValue = isTemplateXflowMessage
				? 'personalized'
				: statsig.getExperimentValue(
						{ identifiers: { tenantId } },
						{ name: MISSING_APP_EXPERIMENT, parameter: 'arm', defaultValue: 'control' },
					);
			const arm: ExperimentArm =
				armValue === 'generic' || armValue === 'personalized' ? armValue : 'control';
			const resolvedRecommendedApp: RecommendedApp = isTemplateXflowMessage
				? 'loom'
				: (recommendedApp as RecommendedApp);

			const homeUrl = () => getAtlassianHomeUrl(env);
			const resolvedTenantId = typeof targetTenantId === 'string' ? targetTenantId : tenantId;
			const templateXflowModalUrl =
				isTemplateXflowMessage && resolvedOrgId
					? getHomeTwcXflowModalLink(env, resolvedOrgId, tenantId)
					: undefined;

			const commonReturn = {
				...message.context,
				...(message.context.triggerType
					? { context: { triggerType: message.context.triggerType } }
					: {}),
				arm,
				recommendedApp: resolvedRecommendedApp,
				actionTier,
				targetTenantId,
				collectionEntitlementId,
				messageInstanceId,
				locale,
			};

			switch (arm) {
				case 'control': {
					logger.info(
						{ messageInstanceId, messageTemplateId, triggerId, arm },
						'Control arm — aborting hydration',
					);
					request.metrics
						.counter('twc-sidenav-banner.conf.info.count', { reason: 'control-arm' })
						.incr();
					throw new HydrationAbortError('Control cohort - abort hydration');
				}

				case 'generic': {
					const redirectTo = resolveRedirectTo(message.context.redirectTo);
					let onboardingUrl = await getOnboardingUrl({
						redirectTo,
						requestContext: request,
						messageInstanceId,
						messageTemplateId,
						orgId: orgId as string | undefined,
						twcEntitlementId: twcEntitlementId as string | undefined,
						tenantId: resolvedTenantId,
					});

					if (!onboardingUrl) {
						onboardingUrl = homeUrl();
						logger.info(
							{ messageTemplateId, messageInstanceId, triggerId, arm },
							'No onboarding URL found for generic arm — defaulting to Atlassian Home',
						);
						request.metrics
							.counter('twc-sidenav-banner.conf.info.count', {
								reason: 'generic-defaulted-to-atlassian-home',
							})
							.incr();
					}

					request.metrics.counter('twc-sidenav-banner.conf.success.count', { arm }).incr();

					return { ...commonReturn, redirectTo, onboardingUrl };
				}

				case 'personalized': {
					const isLoomPersonalized = resolvedRecommendedApp === 'loom';
					const redirectTo: 'jira' | 'confluence' =
						resolvedRecommendedApp === 'confluence' ? 'confluence' : 'jira';

					let onboardingUrl: string | undefined;

					if (templateXflowModalUrl) {
						onboardingUrl = templateXflowModalUrl;
					} else if (isLoomPersonalized) {
						onboardingUrl = getLoomOnboardingUrl(env);
					} else {
						onboardingUrl = await getOnboardingUrl({
							redirectTo,
							requestContext: request,
							messageInstanceId,
							messageTemplateId,
							orgId: orgId as string | undefined,
							twcEntitlementId: twcEntitlementId as string | undefined,
							tenantId: resolvedTenantId,
						});
					}

					if (!onboardingUrl) {
						onboardingUrl = homeUrl();
						logger.info(
							{
								messageTemplateId,
								messageInstanceId,
								triggerId,
								recommendedApp: resolvedRecommendedApp,
							},
							'No onboarding URL found for personalized arm — defaulting to Atlassian Home',
						);
						request.metrics
							.counter('twc-sidenav-banner.conf.info.count', {
								reason: 'personalized-defaulted-to-atlassian-home',
							})
							.incr();
					}

					request.metrics.counter('twc-sidenav-banner.conf.success.count', { arm }).incr();

					return { ...commonReturn, redirectTo, onboardingUrl };
				}
			}
		},
	},
}));

const BannerContent = ({
	heading,
	handleOnDismissed,
	onboardingUrl,
	messageInstanceId,
	locale,
	arm,
	redirectTo,
	recommendedApp,
	experimentKey,
	actionTier,
	targetTenantId,
	collectionEntitlementId,
}: MessageFrontendProps & { heading?: string }) => {
	const { handleDismiss } = useHandleDismiss({
		onDismissed: handleOnDismissed,
		clickIdentifier:
			arm === 'personalized'
				? 'twc-sidenav-banner-personalized-dismiss'
				: arm === 'generic'
					? 'twc-sidenav-banner-generic-dismiss'
					: 'twc-sidenav-banner-dismiss',
	});

	if (arm === 'personalized' && recommendedApp && actionTier) {
		return (
			<PersonalizedBanner
				heading={heading}
				locale={locale}
				recommendedApp={recommendedApp as RecommendedApp}
				onClose={handleDismiss}
				onCtaClick={handleDismiss}
				href={onboardingUrl}
				messageInstanceId={messageInstanceId}
				experimentKey={experimentKey}
				actionTier={actionTier}
				targetTenantId={targetTenantId}
				collectionEntitlementId={collectionEntitlementId}
				surfaceProduct="confluence"
			/>
		);
	}

	if (arm === 'generic') {
		return (
			<GenericBanner
				heading={heading}
				locale={locale}
				product={redirectTo}
				onClose={handleDismiss}
				onCtaClick={handleDismiss}
				href={onboardingUrl}
				messageInstanceId={messageInstanceId}
				surfaceProduct="confluence"
			/>
		);
	}

	return null;
};

const TemplateXflowBannerContent = ({
	heading,
	blueprintModuleCompleteKey,
	...props
}: MessageFrontendProps & {
	heading: string;
	blueprintModuleCompleteKey: string;
}) => {
	const { formatMessage } = useIntl();
	const { messageDismissed, messageSnoozed } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const additionalMetadataFromFrontend = {
		promptSurface: 'sideNavBanner',
		sourceTemplateEntityId: blueprintModuleCompleteKey,
	};

	const handleDismiss = () => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'twcg2112Dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: { actionType: 'dismiss' },
			additionalMetadataFromFrontend,
		});
		void messageDismissed();
		props.handleOnDismissed();
	};

	const handleTryItFree = () => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'twcg2112TryItFree',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: { actionType: 'open' },
			additionalMetadataFromFrontend,
		});
		void messageDismissed();
		props.handleOnDismissed();
	};

	const handleNotNow = () => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'twcg2112RemindMeLater',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: { actionType: 'snooze', snoozeDuration: 1 },
			additionalMetadataFromFrontend,
		});
		void messageSnoozed(1);
		props.handleOnDismissed();
	};

	return (
		<PersonalizedBanner
			heading={heading}
			locale={props.locale}
			recommendedApp="loom"
			onClose={handleDismiss}
			onCtaClick={handleTryItFree}
			ctaLabel={formatMessage(templateXflowMessages.tryItFree)}
			secondaryActionLabel={formatMessage(templateXflowMessages.notNow)}
			onSecondaryAction={handleNotNow}
			href={props.onboardingUrl}
			messageInstanceId={props.messageInstanceId}
			experimentKey={props.experimentKey}
			actionTier={props.actionTier}
			targetTenantId={props.targetTenantId}
			collectionEntitlementId={props.collectionEntitlementId}
			surfaceProduct="confluence"
		/>
	);
};

const TemplateXflowBanner = (props: MessageFrontendProps) => {
	const { arm, recommendedApp, actionTier, tapCohortTrait, waitForTrigger } = props;
	const { formatMessage } = useIntl();
	const { isActive: isTemplateViewTriggerActive } = useTrigger(TEMPLATE_VIEW_TRIGGER_ID);
	const { isActive: isTemplateAppliedTriggerActive } = useTrigger(TEMPLATE_APPLIED_TRIGGER_ID);
	const { fireTrigger: fireTemplateViewTrigger } = useFirePostOfficeTrigger({
		triggerId: TEMPLATE_VIEW_TRIGGER_ID,
	});
	const { fireTrigger: fireTemplateAppliedTrigger } = useFirePostOfficeTrigger({
		triggerId: TEMPLATE_APPLIED_TRIGGER_ID,
	});
	const { templateXflow } = useContext(ConfluenceExperimentsContext);
	const currentPageTemplateData = templateXflow?.currentPageTemplateData;
	const blueprintModuleCompleteKey = currentPageTemplateData?.blueprintModuleCompleteKey;
	const templateHeadingDescriptor = blueprintModuleCompleteKey
		? templateXflowHeadingByBlueprintModuleCompleteKey[blueprintModuleCompleteKey]
		: undefined;
	const templateHeading = templateHeadingDescriptor
		? formatMessage(templateHeadingDescriptor)
		: undefined;
	const isTemplateXflowCohortEligible =
		currentPageTemplateData?.isEligible === true || tapCohortTrait === 'variation';
	const isTemplateTriggerActive =
		Boolean(isTemplateViewTriggerActive) || Boolean(isTemplateAppliedTriggerActive);
	const hasBannerContent =
		(arm === 'personalized' && Boolean(recommendedApp) && Boolean(actionTier)) || arm === 'generic';
	const shouldStartTemplateXflowDelay =
		fg(TEMPLATE_XFLOW_GATE) &&
		isTemplateXflowCohortEligible &&
		isTemplateTriggerActive &&
		Boolean(currentPageTemplateData?.contentId) &&
		Boolean(templateHeadingDescriptor) &&
		hasBannerContent;
	const [hasTemplateXflowDelayElapsed, setHasTemplateXflowDelayElapsed] = useState(false);

	useEffect(() => {
		setHasTemplateXflowDelayElapsed(false);

		if (!shouldStartTemplateXflowDelay) {
			return;
		}

		const timeoutId = setTimeout(() => {
			setHasTemplateXflowDelayElapsed(true);
		}, TEMPLATE_XFLOW_DELAY_MS);

		return () => clearTimeout(timeoutId);
	}, [
		blueprintModuleCompleteKey,
		currentPageTemplateData?.contentId,
		shouldStartTemplateXflowDelay,
	]);

	const resetTemplateTriggers = useCallback(() => {
		fireTemplateViewTrigger({ isActive: false });
		fireTemplateAppliedTrigger({ isActive: false });
	}, [fireTemplateAppliedTrigger, fireTemplateViewTrigger]);

	if (!templateHeading || !blueprintModuleCompleteKey || !waitForTrigger || !hasBannerContent) {
		return null;
	}

	return waitForTrigger({
		isTriggerActive: hasTemplateXflowDelayElapsed,
		messageContent: (
			<TemplateXflowBannerContent
				{...props}
				heading={templateHeading}
				blueprintModuleCompleteKey={blueprintModuleCompleteKey}
			/>
		),
		onTeardown: resetTemplateTriggers,
	});
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: (props: MessageFrontendProps) => {
			const { experimentKey, explicitEnableRequired } = props;
			const isTemplateXflowMessage =
				experimentKey === TEMPLATE_XFLOW_EXPERIMENT_KEY && explicitEnableRequired === true;

			if (isTemplateXflowMessage) {
				return <TemplateXflowBanner {...props} />;
			}

			return <BannerContent {...props} />;
		},
	},
});
